import React, { useCallback, useState, useRef, useEffect } from 'react';
import { ModalContent, ModalForm, Label, ModalButton, DropDiv } from './styles';
import Button from '../../button';
import { UserInterface } from '../../../interfaces/chatMessageInterface';
import { rootStore } from '../../../mobx/rootStore';
import { Dropdown, DropdownItem } from '../../dropdown';
import { useMobileDevice } from '../../../global/useMobile';
import { UserModel } from '../../../mobx/models/user';
import { BUTTON_TYPE, USER_ROLE } from '../../../global/constants';
import ModalHeader from '../modalHeader';
import { observer } from 'mobx-react-lite';
import Input from '../../input';

interface Props {
  onClose?: () => void;
  heading?: string;
  label?: string;
  isEditGroupModal?: boolean;
  usersList?: UserModel[];
  onChange?: (propsName: string, value: string) => void;
  groupUserList?: UserInterface[];
  onAddMembers?: (e: React.SyntheticEvent, users: string[]) => void;
  loading?: boolean;
  onEditGroupName?: (e: React.SyntheticEvent) => void;
}

const AddGroupMemberModal: React.FC<Props> = (props) => {
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isInputFocused, setIsInputFocused] = useState(false);
  const [groupName, setGroupName] = useState('');

  const dropdownRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownInputRef = useRef<{
    closeDropdown: () => void;
  }>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const isMobile = useMobileDevice();
  const isMinWidth = isMobile ? Math.max(window.innerWidth - 65) : 368;

  // Mobx variable
  const { currentUser, chatMessageStore } = rootStore;
  const { inputChatGroup } = chatMessageStore || {};
  const isAdminOrOwner = currentUser?.isAdmin || currentUser?.isOwner;

  useEffect(() => {
    if (props.isEditGroupModal) {
      setGroupName(String(inputChatGroup?.name || ''));
    }
  }, [props.isEditGroupModal, inputChatGroup?.name]);

  //Other variable
  const existUsersIds = props?.groupUserList?.map((user) => user?.id);
  const filteredUsersList = props?.usersList?.filter((user) => {
    // Filter out agent users
    if (user?.id?.startsWith('agent_')) return false;

    // Filter out already selected users
    if (existUsersIds?.includes(user?.id)) return false;

    // If current user is a client, only show admin/owner users
    if (currentUser?.role === USER_ROLE.CLIENT) {
      return user.isAdmin || user.isOwner;
    }
    // For non-client users, show all users except clients (unless admin/owner)
    if (user.role !== USER_ROLE.CLIENT) return true;
    // Only include client users if current user is admin/owner
    return isAdminOrOwner;
  });

  const handleSelectChangeUsers = useCallback(
    (userId: string) => {
      if (selectedUsers.includes(userId)) {
        setSelectedUsers(selectedUsers.filter((id) => id !== userId));
      } else {
        setSelectedUsers([...selectedUsers, userId]);
        setSearchTerm('');
      }
    },
    [selectedUsers]
  );

  const handleRemoveUser = useCallback(
    (userId: string) => {
      setSelectedUsers(selectedUsers.filter((id) => id !== userId));
    },
    [selectedUsers]
  );

  const scrollToItem = useCallback((index: number) => {
    const item = itemRefs.current[index];
    if (item && dropdownRef.current) {
      const dropdownRect = dropdownRef.current.getBoundingClientRect();
      const itemRect = item.getBoundingClientRect();
      const itemHeight = itemRect.height;

      // Calculate the position to scroll to
      const scrollTop = dropdownRef.current.scrollTop;
      const itemTop = itemRect.top - dropdownRect.top + scrollTop;
      const itemBottom = itemTop + itemHeight;
      const dropdownHeight = dropdownRect.height;

      // If item is below the visible area
      if (itemBottom > dropdownHeight + scrollTop) {
        dropdownRef.current.scrollTop = itemBottom - dropdownHeight;
      }
      // If item is above the visible area
      else if (itemTop < scrollTop) {
        dropdownRef.current.scrollTop = itemTop;
      }
    }
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      const filteredUsers =
        filteredUsersList?.filter(
          (user) =>
            user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            user.email?.toLowerCase().includes(searchTerm.toLowerCase())
        ) || [];

      switch (e.key) {
        case 'ArrowUp':
          e.preventDefault();
          setCurrentIndex((prevIndex) => {
            const newIndex = prevIndex === 0 ? filteredUsers.length - 1 : prevIndex - 1;
            scrollToItem(newIndex);
            return newIndex;
          });
          break;
        case 'ArrowDown':
          e.preventDefault();
          setCurrentIndex((prevIndex) => {
            const newIndex = prevIndex === filteredUsers.length - 1 ? 0 : prevIndex + 1;
            scrollToItem(newIndex);
            return newIndex;
          });
          break;
        case 'Enter':
          e.preventDefault();
          if (filteredUsers[currentIndex]) {
            handleSelectChangeUsers(filteredUsers[currentIndex].id);
          }
          break;
        case 'Escape':
          e.preventDefault();
          setIsDropdownOpen(false);
          break;
        case 'Backspace':
          if (searchTerm === '' && selectedUsers.length > 0) {
            setSelectedUsers(selectedUsers.slice(0, -1));
          }
          break;
        default:
          break;
      }
    },
    [currentIndex, filteredUsersList, searchTerm, selectedUsers, handleSelectChangeUsers, scrollToItem]
  );

  useEffect(() => {
    if (isDropdownOpen) {
      setCurrentIndex(0);
      // Reset item refs array
      itemRefs.current = [];
      // Focus the dropdown
      dropdownRef.current?.focus();
    }
  }, [isDropdownOpen]);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    requestAnimationFrame(() => {
      inputRef.current?.focus();
    });
  }, []);

  const handelDropdownIconClick = useCallback(
    (e: React.SyntheticEvent) => {
      if (!isDropdownOpen) return;
      e.preventDefault();
      e.stopPropagation();
      dropdownInputRef?.current?.closeDropdown();
    },
    [isDropdownOpen]
  );

  return (
    <>
      <ModalHeader title={props?.heading} onClose={props?.onClose} />
      <ModalContent
        onSubmit={(e: React.SyntheticEvent) =>
          !props?.isEditGroupModal && props?.onAddMembers
            ? props?.onAddMembers(e, selectedUsers)
            : props?.onEditGroupName && props?.onEditGroupName(e)
        }>
        <ModalForm>
          <Label>{props?.label}</Label>
          {props?.isEditGroupModal ? (
            <Input
              placeHolder='Enter group name'
              value={groupName}
              onChange={(e) => {
                const value = e.target.value;
                setGroupName(value);
                props?.onChange && props.onChange('name', value);
              }}
            />
          ) : (
            <Dropdown
              ref={dropdownInputRef}
              isMinWidth={isMinWidth}
              closeOnItemClick={false}
              updateDropdownOpen={(value) => setIsDropdownOpen(value)}
              content={
                <Input
                  inputType='tags'
                  multiSelectProps={{
                    isActive: isDropdownOpen || isInputFocused,
                    placeholderWhenEmpty: 'Enter member name',
                    selectedUsers: selectedUsers.map((userId) => {
                      return {
                        id: userId,
                        displayName: filteredUsersList?.find((user) => user.id === userId)?.name || ''
                      };
                    }),
                    value: searchTerm,
                    onChange: handleInputChange,
                    onKeyDown: handleKeyDown,
                    onFocus: () => setIsInputFocused(true),
                    onBlur: () => setIsInputFocused(false),
                    showIcon: true,
                    isDropdownOpen: isDropdownOpen,
                    onIconClick: handelDropdownIconClick,
                    onRemoveUser: (user, e) => {
                      handleRemoveUser(user.id as string);
                    }
                  }}
                />
              }
              trigger='click'>
              <DropDiv ref={dropdownRef} tabIndex={0} onKeyDown={handleKeyDown}>
                {filteredUsersList
                  ?.filter(
                    (user) =>
                      user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                      user.email?.toLowerCase().includes(searchTerm.toLowerCase())
                  )
                  .map((user, index) => {
                    const isSelected = selectedUsers.includes(user.id);
                    return (
                      <div key={user.id} ref={(el) => (itemRefs.current[index] = el)}>
                        <DropdownItem
                          isChecked={isSelected}
                          label={user.name}
                          isAvatar={true}
                          avatarSrc={user.profile_photo || ''}
                          onClick={() => handleSelectChangeUsers(user.id)}
                          isActive={isSelected || index === currentIndex}
                        />
                      </div>
                    );
                  })}
              </DropDiv>
            </Dropdown>
          )}
        </ModalForm>
        <ModalButton>
          <Button variant={BUTTON_TYPE.SECONDARY} title='Cancel' onClick={props?.onClose} />
          <Button
            variant={BUTTON_TYPE.PRIMARY}
            buttonType={'submit'}
            title={props.isEditGroupModal ? 'Save' : 'Add'}
            isLoading={props?.loading}
          />
        </ModalButton>
      </ModalContent>
    </>
  );
};

export default observer(AddGroupMemberModal);
